import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Lock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PIN = '2507';

const APP_NAMES = {
  wagvault: 'Vault', vault: 'Vault', magvault: 'Vault',
  bars: 'Bars', beam: 'Beam',
  wagfloor: 'Floor', floor: 'Floor', magfloor: 'Floor',
  range: 'R&C', randc: 'R&C',
  pommel: 'Pommel', rings: 'Rings', pbars: 'P-Bars', hbar: 'High Bar',
  mag: 'MAG',
};
const APP_ORDER = [
  'wagvault','vault','magvault','bars','beam','wagfloor','floor','magfloor',
  'range','randc','pommel','rings','pbars','hbar','mag',
];
const PANEL_LABELS = { single: '', SINGLE: '', A: 'Panel A', B: 'Panel B' };

function sortCodes(codes) {
  const byApp = {};
  for (const c of codes) {
    const app = (c.apparatus || '').toLowerCase();
    (byApp[app] = byApp[app] || []).push(c);
  }
  const sortedApps = Object.keys(byApp).sort(
    (a, b) => (APP_ORDER.indexOf(a) + 1 || 99) - (APP_ORDER.indexOf(b) + 1 || 99)
  );
  const result = [];
  for (const app of sortedApps) {
    const appCodes = byApp[app].sort((a, b) =>
      String(a.panel || '').localeCompare(String(b.panel || ''))
    );
    result.push(...appCodes.map(c => ({ ...c, app })));
  }
  return result;
}

function CountPicker({ label, value, max, onChange }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] font-bold text-gray-400 w-3">{label}</span>
      <div className="flex gap-0.5">
        {Array.from({ length: max }, (_, i) => i + 1).map(n => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className={`w-6 h-6 rounded text-xs font-bold transition-colors ${
              value === n ? 'bg-black text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ScoringControlPage() {
  const [pin, setPin] = useState('');
  const [authed, setAuthed] = useState(false);
  const [pinError, setPinError] = useState(false);

  const [competitions, setCompetitions] = useState([]);
  const [selectedComp, setSelectedComp] = useState('');
  const [codes, setCodes] = useState(null);
  const [loading, setLoading] = useState(false);
  // judgeConfig: { "apparatus|panel" : {dJudges, eJudges} }
  const [judgeConfig, setJudgeConfig] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!authed) return;
    axios.get(`${API}/registration/competitions`)
      .then(r => setCompetitions(r.data || []))
      .catch(() => {});
  }, [authed]);

  const loadCompData = useCallback((compId) => {
    if (!compId) { setCodes(null); setJudgeConfig({}); return; }
    setLoading(true);
    Promise.all([
      axios.get(`${API}/scoring/judge-codes-public/${compId}`),
      axios.get(`${API}/scoring/judge-config/${compId}`),
    ]).then(([codesRes, configRes]) => {
      setCodes(codesRes.data.codes || []);
      // Build local config map — normalise panel labels so "SINGLE PANEL" and "single" share a key
      const normPanel = (p) => {
        if (!p) return 'single';
        const u = String(p).trim().toUpperCase();
        if (u === 'SINGLE' || u === 'SINGLE PANEL') return 'single';
        return u;
      };
      const map = {};
      for (const cfg of (configRes.data.configs || [])) {
        map[`${cfg.apparatus}|${normPanel(cfg.panel)}`] = { dJudges: cfg.dJudges || 1, eJudges: cfg.eJudges || 1 };
      }
      setJudgeConfig(map);
    }).catch(() => { setCodes([]); setJudgeConfig({}); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadCompData(selectedComp); }, [selectedComp, loadCompData]);

  const updateCount = async (apparatus, panel, field, val) => {
    // Normalise panel so "SINGLE PANEL" and "single" use the same key
    const normPanel = (p) => {
      if (!p) return 'single';
      const u = String(p).trim().toUpperCase();
      if (u === 'SINGLE' || u === 'SINGLE PANEL') return 'single';
      return u;
    };
    const key = `${apparatus}|${normPanel(panel)}`;
    const next = { ...judgeConfig, [key]: { ...(judgeConfig[key] || { dJudges: 1, eJudges: 1 }), [field]: val } };
    setJudgeConfig(next);
    // Auto-save
    setSaving(true);
    const configs = Object.entries(next).map(([k, v]) => {
      const [app, pnl] = k.split('|');
      return { apparatus: app, panel: pnl, ...v };
    });
    try {
      await axios.put(`${API}/scoring/judge-config/${selectedComp}`, { configs });
    } catch (e) { /* silent */ }
    setSaving(false);
  };

  const handlePin = (e) => {
    e.preventDefault();
    if (pin === PIN) { setAuthed(true); setPinError(false); }
    else { setPinError(true); setPin(''); }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-full max-w-sm px-6">
          <div className="flex justify-center mb-8">
            <img src="/scorz-logo.png" alt="Scorz" className="h-14 w-auto" />
          </div>
          <h1 className="text-2xl font-black text-center text-gray-900 mb-1">Scoring Control</h1>
          <p className="text-center text-gray-400 mb-8 text-sm">Enter PIN to access</p>
          <form onSubmit={handlePin} className="space-y-3">
            <input
              type="password"
              value={pin}
              onChange={e => setPin(e.target.value)}
              placeholder="••••"
              maxLength={4}
              autoFocus
              className={`w-full border-2 rounded-2xl px-5 py-4 text-center text-3xl tracking-widest focus:outline-none transition-colors ${
                pinError ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-black'
              }`}
            />
            {pinError && <p className="text-red-500 text-sm text-center">Incorrect PIN</p>}
            <button type="submit" className="w-full bg-black hover:bg-gray-800 text-white rounded-2xl py-4 text-lg font-bold transition-all">
              Unlock
            </button>
          </form>
        </div>
      </div>
    );
  }

  const allCodes = codes ? sortCodes(codes) : [];
  const hasNoComp = !selectedComp;
  const hasNoCodes = selectedComp && codes !== null && codes.length === 0;
  const hasCodes = selectedComp && allCodes.length > 0;

  return (
    <div className="min-h-screen bg-white">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <img src="/scorz-logo.png" alt="Scorz" className="h-9 w-auto" />
          <span className="text-base font-black text-gray-900 tracking-tight">Scoring Control</span>
          {saving && <span className="text-xs text-gray-400 ml-2">Saving…</span>}
        </div>
        <Lock className="w-4 h-4 text-gray-300" />
      </div>

      <div className="px-6 py-5">
        <div className="mb-6 max-w-sm">
          <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Competition</label>
          <select
            value={selectedComp}
            onChange={e => { setSelectedComp(e.target.value); setCodes(null); }}
            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm font-medium focus:outline-none focus:border-black transition-colors bg-white"
            data-testid="scoring-control-comp-select"
          >
            <option value="">Select a competition…</option>
            {competitions.map(c => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>
        </div>

        {hasNoComp && (
          <div className="text-center py-16 text-gray-300 text-lg font-medium select-none">
            Select a competition above
          </div>
        )}

        {loading && (
          <div className="text-center py-16">
            <div className="w-8 h-8 border-4 border-gray-200 border-t-black rounded-full animate-spin mx-auto" />
          </div>
        )}

        {hasNoCodes && !loading && (
          <div className="border-2 border-dashed border-gray-200 rounded-2xl p-10 text-center">
            <p className="text-lg font-bold text-gray-700 mb-2">No judge codes generated yet</p>
            <p className="text-gray-400 text-sm mb-5">Generate codes first from SuperAdmin → Scoring System Management.</p>
            <a href="/scorz-superadmin" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-black text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-gray-800 transition-colors">
              Open SuperAdmin
            </a>
          </div>
        )}

        {hasCodes && !loading && (
          <>
            <p className="text-xs text-gray-400 mb-3 uppercase tracking-widest font-bold">
              Judge Setup — select number of D and E judges per panel
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {allCodes.map(c => {
                const appLabel = APP_NAMES[c.app] || c.app.toUpperCase();
                const rawPanel = String(c.panel || '');
                const panelLabel = rawPanel in PANEL_LABELS ? PANEL_LABELS[rawPanel] : rawPanel;
                // Normalise panel for config map lookup: "SINGLE PANEL" / "SINGLE" → "single"
                const np = (() => { const u = rawPanel.trim().toUpperCase(); return (u === 'SINGLE' || u === 'SINGLE PANEL') ? 'single' : rawPanel; })();
                const key = `${c.app}|${np}`;
                const cfg = judgeConfig[key] || { dJudges: 1, eJudges: 1 };

                return (
                  <div
                    key={key}
                    data-testid={`code-card-${c.app}-${rawPanel}`}
                    className="border border-gray-200 rounded-xl p-3 flex flex-col gap-2.5 hover:border-gray-300 transition-colors bg-white"
                  >
                    {/* Apparatus label + code on same row */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs font-bold text-gray-500 uppercase tracking-wide leading-tight">
                        {appLabel}
                        {panelLabel && <span className="text-gray-400 font-normal ml-1">· {panelLabel}</span>}
                      </div>
                      <div className="font-mono font-black text-xl text-gray-900 tracking-widest shrink-0">
                        {c.code}
                      </div>
                    </div>

                    {/* D / E judge count pickers */}
                    <div className="flex flex-col gap-1.5 pt-1 border-t border-gray-100">
                      <CountPicker
                        label="D"
                        value={cfg.dJudges}
                        max={3}
                        onChange={v => updateCount(c.app, np, 'dJudges', v)}
                      />
                      <CountPicker
                        label="E"
                        value={cfg.eJudges}
                        max={5}
                        onChange={v => updateCount(c.app, np, 'eJudges', v)}
                      />
                    </div>

                    {c.route && (
                      <div className="text-[10px] text-gray-300 truncate">{c.route}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
